// Products: add a new printed tee to the website without opening Shopify.
//
// A new tee starts as a copy of a live tee of the same kind, so its sizes,
// sales channels, tags and description layout are already right. Staff then
// give it its own name, blank colour, price, photos and DTF file, and publish.
// The server keeps it inside the stock rules (routes/productStudio.js) and
// the office PC files the DTF design as <code>.png in Print Ready Designs.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../api';

const BASE = `${API}/api/products-studio`;
const BLANKS = ['Black', 'Off White', 'Blue', 'Maroon', 'Beige', 'Pink'];
const errMsg = (e) => (e.response && e.response.data && e.response.data.message) || e.message;

const box = { border: '1px solid var(--line)', borderRadius: 10, padding: 14, background: 'var(--surface)' };
const label = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-soft)', margin: '10px 0 4px' };
const full = { width: '100%', boxSizing: 'border-box' };
const ghost = {
  background: 'var(--surface)', color: 'var(--wine)', border: '1px solid var(--wine)',
  borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 13
};

function StatusBadge({ status }) {
  if (status === 'ACTIVE') return <span className="badge badge-green">Live</span>;
  if (status === 'DRAFT') return <span className="badge badge-yellow">Draft</span>;
  if (status === 'ARCHIVED') return <span className="badge badge-blue">Archived</span>;
  return <span className="badge badge-red">Deleted on Shopify</span>;
}

function artworkText(a) {
  if (!a) return { text: 'No DTF file yet', cls: 'badge-red' };
  if (a.status === 'saved') return { text: 'Saved on office PC', cls: 'badge-green' };
  if (a.status === 'failed') return { text: 'Office PC could not save it', cls: 'badge-red' };
  return { text: 'Waiting for office PC', cls: 'badge-yellow' };
}

function Banner({ kind, children, onClose }) {
  const colours = kind === 'error'
    ? { background: '#fbe9e7', color: '#8c1d18' }
    : kind === 'warn' ? { background: 'var(--chartreuse-pale)', color: 'var(--olive)' }
    : { background: '#e8f3ec', color: '#2e7d50' };
  return (
    <div style={{ ...colours, borderRadius: 8, padding: '10px 12px', margin: '10px 0', fontSize: 13, display: 'flex', gap: 10 }}>
      <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{children}</div>
      {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>}
    </div>
  );
}

// ── list ──────────────────────────────────────────────────────────────────

function ProductList({ onOpen, onNew }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios.get(BASE).then(r => setRows(r.data)).catch(e => setError(errMsg(e)));
  }, []);
  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div className="card-title" style={{ flex: 1, margin: 0 }}>New tees</div>
        <button className="btn-primary" onClick={onNew}>+ Add a new tee</button>
      </div>
      {error && <Banner kind="error">{error}</Banner>}
      {!rows && !error && <p style={{ color: 'var(--muted)' }}>Loading…</p>}
      {rows && rows.length === 0 && (
        <p style={{ color: 'var(--muted)' }}>Nothing added from here yet. Tap “Add a new tee” to start.</p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginTop: 12 }}>
        {(rows || []).map(r => {
          const a = artworkText(r.artworkStatus ? { status: r.artworkStatus } : null);
          return (
            <button key={r.id} onClick={() => onOpen(r.id)} style={{ ...box, textAlign: 'left', cursor: 'pointer', padding: 0, overflow: 'hidden' }}>
              <div style={{ aspectRatio: '1', background: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {r.image
                  ? <img src={r.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ color: 'var(--muted-soft)', fontSize: 13 }}>No photos yet</span>}
              </div>
              <div style={{ padding: 10 }}>
                <div style={{ fontWeight: 700, color: 'var(--ink)' }}>{r.title}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0 6px' }}>{r.designCode} · {r.blankColor} blank</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <StatusBadge status={r.status} />
                  <span className={'badge ' + a.cls}>{a.text}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── create ────────────────────────────────────────────────────────────────

function NewProduct({ onCreated, onCancel }) {
  const [templates, setTemplates] = useState(null);
  const [form, setForm] = useState({ title: '', designCode: '', blankColor: 'Black', templateId: '', price: '', compareAtPrice: '' });
  const [problem, setProblem] = useState(null);
  const [codeTouched, setCodeTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${BASE}/templates`).then(r => {
      setTemplates(r.data);
      const first = r.data.find(t => /oversized/i.test(t.productType || '')) || r.data[0];
      if (first) setForm(f => ({ ...f, templateId: first.id, price: first.price || '', compareAtPrice: first.compareAtPrice || '' }));
    }).catch(e => setError(errMsg(e)));
  }, []);

  // Check the name and propose a design code as it is typed.
  useEffect(() => {
    const t = form.title.trim();
    if (!t) { setProblem(null); return; }
    const timer = setTimeout(() => {
      axios.get(`${BASE}/suggest`, { params: { title: t } }).then(r => {
        setProblem(r.data.problem);
        if (!codeTouched && r.data.designCode) setForm(f => ({ ...f, designCode: r.data.designCode }));
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [form.title, codeTouched]);

  const pickTemplate = (id) => {
    const t = (templates || []).find(x => x.id === id);
    setForm(f => ({ ...f, templateId: id, price: t ? t.price || '' : f.price, compareAtPrice: t ? t.compareAtPrice || '' : f.compareAtPrice }));
  };

  const create = async () => {
    setBusy(true); setError(null);
    try {
      const r = await axios.post(BASE, form);
      onCreated(r.data.id, r.data.warnings);
    } catch (e) { setError(errMsg(e)); }
    setBusy(false);
  };

  const template = (templates || []).find(t => t.id === form.templateId);
  return (
    <div className="card" style={{ maxWidth: 640 }}>
      <div className="card-title">Add a new tee</div>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0 }}>
        It starts as a hidden draft copied from a tee already on the site, so sizes and sales channels are set up for you.
        Nothing shows on the website until you press Publish.
      </p>

      <label style={label}>Name on the website</label>
      <input style={full} value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Life is short" />
      {problem && <Banner kind="error">{problem}</Banner>}

      <label style={label}>Copy setup from</label>
      <select style={full} value={form.templateId} onChange={e => pickTemplate(e.target.value)}>
        {!templates && <option>Loading…</option>}
        {(templates || []).map(t => (
          <option key={t.id} value={t.id}>{t.title} — {t.productType || 'Tee'} · ₹{t.price}</option>
        ))}
      </select>
      {template && template.image && (
        <img src={template.image} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, marginTop: 6 }} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={label}>Blank colour</label>
          <select style={full} value={form.blankColor} onChange={e => set('blankColor', e.target.value)}>
            {BLANKS.map(b => <option key={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label style={label}>Design code (DTF file name)</label>
          <input style={full} value={form.designCode}
            onChange={e => { setCodeTouched(true); set('designCode', e.target.value.toUpperCase()); }} />
        </div>
        <div>
          <label style={label}>Price (₹)</label>
          <input style={full} type="number" value={form.price} onChange={e => set('price', e.target.value)} />
        </div>
        <div>
          <label style={label}>MRP / compare-at (₹)</label>
          <input style={full} type="number" value={form.compareAtPrice} onChange={e => set('compareAtPrice', e.target.value)} />
        </div>
      </div>

      {error && <Banner kind="error">{error}</Banner>}
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button className="btn-primary" disabled={busy || !!problem || !form.title.trim() || !form.templateId} onClick={create}>
          {busy ? 'Creating draft…' : 'Create draft'}
        </button>
        <button style={ghost} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── photos ────────────────────────────────────────────────────────────────

function Photos({ id, media, onChange, setError }) {
  const [order, setOrder] = useState(media);
  const [uploading, setUploading] = useState(null);
  const [saving, setSaving] = useState(false);
  const dragFrom = useRef(null);
  const input = useRef(null);
  useEffect(() => { setOrder(media); }, [media]);

  const upload = async (files) => {
    const list = Array.from(files || []);
    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      setUploading(`Uploading ${i + 1} of ${list.length}: ${f.name}`);
      try {
        const r = await axios.post(`${BASE}/${id}/images`, f, {
          headers: { 'Content-Type': f.type || 'image/jpeg', 'x-filename': encodeURIComponent(f.name) }
        });
        onChange(r.data);
      } catch (e) { setError(`${f.name}: ${errMsg(e)}`); }
    }
    setUploading(null);
    if (input.current) input.current.value = '';
  };

  const saveOrder = async (next) => {
    setOrder(next); setSaving(true);
    try { onChange((await axios.post(`${BASE}/${id}/images/order`, { mediaIds: next.map(m => m.id) })).data); }
    catch (e) { setError(errMsg(e)); setOrder(media); }
    setSaving(false);
  };
  const move = (from, to) => {
    if (to < 0 || to >= order.length || from === to) return;
    const next = [...order];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    saveOrder(next);
  };
  const remove = async (m) => {
    if (!window.confirm('Remove this photo from the product?')) return;
    try { onChange((await axios.delete(`${BASE}/${id}/images`, { params: { mediaId: m.id } })).data); }
    catch (e) { setError(errMsg(e)); }
  };

  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, flex: 1 }}>Product photos</div>
        <input ref={input} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => upload(e.target.files)} />
        <button className="btn-primary" disabled={!!uploading} onClick={() => input.current.click()}>+ Add photos</button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--muted)', margin: '6px 0 10px' }}>
        The first photo is the main one on the website. Drag to reorder, or use the arrows. {saving && 'Saving order…'}
      </p>
      {uploading && <Banner kind="warn">{uploading}</Banner>}
      {order.length === 0 && !uploading && <p style={{ color: 'var(--muted-soft)', fontSize: 13 }}>No photos yet.</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
        {order.map((m, i) => (
          <div key={m.id} draggable
            onDragStart={() => { dragFrom.current = i; }}
            onDragOver={e => e.preventDefault()}
            onDrop={() => { if (dragFrom.current !== null) move(dragFrom.current, i); dragFrom.current = null; }}
            style={{ border: i === 0 ? '2px solid var(--wine)' : '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', background: 'var(--paper)', cursor: 'grab' }}>
            <div style={{ aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {m.url
                ? <img src={m.url} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ fontSize: 11, color: 'var(--muted)' }}>Processing…</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', padding: 4, gap: 2 }}>
              <button title="Move left" onClick={() => move(i, i - 1)} disabled={i === 0} style={{ ...ghost, padding: '2px 8px', border: 'none' }}>◀</button>
              <span style={{ flex: 1, textAlign: 'center', fontSize: 11, color: 'var(--muted)' }}>{i === 0 ? 'Main' : i + 1}</span>
              <button title="Move right" onClick={() => move(i, i + 1)} disabled={i === order.length - 1} style={{ ...ghost, padding: '2px 8px', border: 'none' }}>▶</button>
              <button title="Remove" onClick={() => remove(m)} style={{ ...ghost, padding: '2px 8px', border: 'none', color: '#b3261e' }}>✕</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── DTF artwork ───────────────────────────────────────────────────────────

function Artwork({ id, code, artwork, onChange, setError }) {
  const [progress, setProgress] = useState(null);
  const input = useRef(null);
  const upload = async (f) => {
    if (!f) return;
    if (!/\.png$/i.test(f.name)) { setError('The DTF design must be a PNG file.'); return; }
    setProgress(0);
    try {
      const r = await axios.post(`${BASE}/${id}/artwork`, f, {
        headers: { 'Content-Type': 'image/png', 'x-filename': encodeURIComponent(f.name) },
        onUploadProgress: (p) => p.total && setProgress(Math.round((p.loaded / p.total) * 100))
      });
      onChange(r.data);
    } catch (e) { setError(errMsg(e)); }
    setProgress(null);
    if (input.current) input.current.value = '';
  };
  const resend = async () => {
    try { onChange((await axios.post(`${BASE}/${id}/artwork/resend`)).data); } catch (e) { setError(errMsg(e)); }
  };

  const a = artwork;
  const s = artworkText(a);
  const cm = (px) => (a && a.dpi ? (px / a.dpi * 2.54).toFixed(1) : null);
  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, flex: 1 }}>DTF design file</div>
        <input ref={input} type="file" accept="image/png" style={{ display: 'none' }} onChange={e => upload(e.target.files[0])} />
        <button className={a ? '' : 'btn-primary'} style={a ? ghost : undefined} disabled={progress !== null}
          onClick={() => input.current.click()}>{a ? 'Replace file' : '+ Upload PNG'}</button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--muted)', margin: '6px 0 10px' }}>
        Print-ready PNG with a transparent background. It is renamed <b>{code}.png</b> and saved in
        OneDrive › Print Ready Designs by the office PC, ready for the gang sheet.
      </p>
      {progress !== null && <Banner kind="warn">Uploading… {progress}%</Banner>}
      {a && (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <span className={'badge ' + s.cls}>{s.text}</span>
          <div>{a.originalName || 'file'} · {(a.bytes / 1048576).toFixed(1)} MB · {a.width} × {a.height} px</div>
          {a.dpi
            ? <div>Print size: <b>{cm(a.width)} × {cm(a.height)} cm</b> at {a.dpi} dpi</div>
            : <div style={{ color: 'var(--olive)' }}>The file has no DPI saved, so its print size is unknown. Check it was exported at 300 dpi.</div>}
          {a.dpi && a.dpi < 300 && <div style={{ color: '#b3261e' }}>Only {a.dpi} dpi — DTF needs 300 dpi or it prints blurry.</div>}
          {a.status === 'saved' && <div style={{ color: 'var(--muted)' }}>Saved as {a.savedPath}</div>}
          {a.status === 'failed' && (
            <div style={{ color: '#b3261e' }}>{a.error} <button style={{ ...ghost, marginLeft: 6 }} onClick={resend}>Try again</button></div>
          )}
          {a.status !== 'saved' && a.status !== 'failed' && (
            <div style={{ color: 'var(--muted)' }}>The office PC picks it up within a few seconds while it is switched on.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── details + collections ─────────────────────────────────────────────────

function Details({ id, data, onChange, setError, setNotice }) {
  const p = data.product;
  const [form, setForm] = useState({});
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setForm({ title: p.title, blankColor: data.blankColor, price: p.price || '', compareAtPrice: p.compareAtPrice || '', descriptionHtml: p.descriptionHtml || '' });
  }, [p.title, p.price, p.compareAtPrice, p.descriptionHtml, data.blankColor]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { onChange((await axios.post(`${BASE}/${id}/details`, form)).data); setNotice('Details saved.'); }
    catch (e) { setError(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div style={box}>
      <div style={{ fontWeight: 700 }}>Details</div>
      <label style={label}>Name on the website</label>
      <input style={full} value={form.title || ''} onChange={e => set('title', e.target.value)} />
      {p.status === 'ACTIVE' && <div style={{ fontSize: 11, color: 'var(--muted)' }}>It is live, so renaming keeps its current web address.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        <div>
          <label style={label}>Blank colour</label>
          <select style={full} value={form.blankColor || ''} onChange={e => set('blankColor', e.target.value)}>
            {BLANKS.map(b => <option key={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label style={label}>Price (₹)</label>
          <input style={full} type="number" value={form.price || ''} onChange={e => set('price', e.target.value)} />
        </div>
        <div>
          <label style={label}>MRP / compare-at (₹)</label>
          <input style={full} type="number" value={form.compareAtPrice || ''} onChange={e => set('compareAtPrice', e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
        <label style={{ ...label, margin: 0, flex: 1 }}>Description</label>
        <button style={{ ...ghost, padding: '3px 10px', fontSize: 12 }} onClick={() => setPreview(v => !v)}>{preview ? 'Edit' : 'Preview'}</button>
      </div>
      {preview
        ? <div style={{ ...box, marginTop: 6, fontSize: 14 }} dangerouslySetInnerHTML={{ __html: form.descriptionHtml || '' }} />
        : <textarea style={{ ...full, minHeight: 180, marginTop: 6, fontFamily: 'monospace', fontSize: 12, padding: 8, border: '1px solid var(--line)', borderRadius: 8 }}
            value={form.descriptionHtml || ''} onChange={e => set('descriptionHtml', e.target.value)} />}
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
        Copied from {data.templateTitle || 'the template'} — change anything about that tee's print to this one.
      </div>
      <button className="btn-primary" style={{ marginTop: 10 }} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save details'}</button>
    </div>
  );
}

function Collections({ id, current, onChange, setError }) {
  const [all, setAll] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${BASE}/collections`).then(r => setAll(r.data)).catch(e => setError(errMsg(e))); }, [setError]);
  useEffect(() => { setPicked(new Set(current.map(c => c.id))); }, [current]);
  const manualIds = new Set((all || []).map(c => c.id));
  const automatic = current.filter(c => all && !manualIds.has(c.id));
  const save = async () => {
    const before = new Set(current.map(c => c.id));
    const add = [...picked].filter(x => !before.has(x));
    const remove = [...before].filter(x => manualIds.has(x) && !picked.has(x));
    setBusy(true);
    try { onChange((await axios.post(`${BASE}/${id}/collections`, { add, remove })).data); }
    catch (e) { setError(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div style={box}>
      <div style={{ fontWeight: 700 }}>Collections</div>
      <p style={{ fontSize: 12, color: 'var(--muted)', margin: '6px 0' }}>
        Hand-picked collections are below. Automatic ones (from tags) fill themselves
        {automatic.length ? ': ' + automatic.map(c => c.title).join(', ') : ''}.
      </p>
      {!all && <p style={{ color: 'var(--muted)' }}>Loading…</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 4 }}>
        {(all || []).map(c => (
          <label key={c.id} style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={picked.has(c.id)}
              onChange={e => setPicked(s => { const n = new Set(s); e.target.checked ? n.add(c.id) : n.delete(c.id); return n; })} />
            {c.title}
          </label>
        ))}
      </div>
      <button style={{ ...ghost, marginTop: 10 }} disabled={busy || !all} onClick={save}>{busy ? 'Saving…' : 'Save collections'}</button>
    </div>
  );
}

// ── editor ────────────────────────────────────────────────────────────────

function Editor({ id, onBack, initialWarnings }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [warnings, setWarnings] = useState(initialWarnings || []);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => axios.get(`${BASE}/${id}`).then(r => setData(r.data)).catch(e => setError(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);

  // Keep refreshing while Shopify processes photos or the office PC saves the file.
  useEffect(() => {
    if (!data || !data.product) return;
    const pending = data.product.media.some(m => !m.url) || (data.artwork && ['waiting', 'claimed'].includes(data.artwork.status));
    if (!pending) return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [data, load]);

  const publish = async (on) => {
    if (on && !window.confirm('Publish this tee on the website now?')) return;
    setBusy(true); setError(null);
    try {
      const r = await axios.post(`${BASE}/${id}/${on ? 'publish' : 'unpublish'}`);
      setData(r.data);
      setWarnings(r.data.warnings || []);
      setNotice(on ? 'Published. Stock appears on the website within a minute.' : 'Hidden from the website (back to draft).');
    } catch (e) { setError(errMsg(e)); }
    setBusy(false);
  };

  if (error && !data) return <div className="card"><button style={ghost} onClick={onBack}>← All tees</button><Banner kind="error">{error}</Banner></div>;
  if (!data) return <div className="card"><p style={{ color: 'var(--muted)' }}>Loading…</p></div>;
  const p = data.product;
  if (!p) return <div className="card"><button style={ghost} onClick={onBack}>← All tees</button><Banner kind="error">This product was deleted on Shopify.</Banner></div>;

  const checks = [
    ['Photos', p.media.length > 0],
    ['DTF file', !!data.artwork],
    ['Price', Number(p.price) > 0]
  ];
  const ready = checks.every(c => c[1]);
  const live = p.status === 'ACTIVE';

  return (
    <div className="card">
      <button style={ghost} onClick={onBack}>← All tees</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '12px 0' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div className="card-title" style={{ margin: 0 }}>{p.title}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{data.designCode} · {data.blankColor} blank · {p.productType}</div>
        </div>
        <StatusBadge status={p.status} />
        {live && p.onlineStoreUrl && <a href={p.onlineStoreUrl} target="_blank" rel="noreferrer" style={ghost}>View on website ↗</a>}
        {!live && p.previewUrl && <a href={p.previewUrl} target="_blank" rel="noreferrer" style={ghost}>Preview ↗</a>}
        {live
          ? <button style={ghost} disabled={busy} onClick={() => publish(false)}>Hide (back to draft)</button>
          : <button className="btn-primary" disabled={busy || !ready} onClick={() => publish(true)}>{busy ? 'Publishing…' : 'Publish'}</button>}
      </div>
      {!live && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
          {checks.map(([name, ok]) => (
            <span key={name} className={'badge ' + (ok ? 'badge-green' : 'badge-red')}>{ok ? '✓' : '✗'} {name}</span>
          ))}
        </div>
      )}
      {error && <Banner kind="error" onClose={() => setError(null)}>{error}</Banner>}
      {notice && <Banner kind="ok" onClose={() => setNotice(null)}>{notice}</Banner>}
      {warnings.length > 0 && <Banner kind="warn" onClose={() => setWarnings([])}>{warnings.join('\n')}</Banner>}

      <div style={{ display: 'grid', gap: 14, marginTop: 14 }}>
        <Photos id={id} media={p.media} onChange={setData} setError={setError} />
        <Artwork id={id} code={data.designCode} artwork={data.artwork} onChange={setData} setError={setError} />
        <Details id={id} data={data} onChange={setData} setError={setError} setNotice={setNotice} />
        <Collections id={id} current={p.collections} onChange={setData} setError={setError} />
        <div style={box}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Stock on the website</div>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 8px' }}>
            Set automatically from the {data.blankColor} blanks on the shelf once it is live. A size with no blanks shows Pre-order.
          </p>
          <table>
            <thead><tr><th>Size</th><th>Stock</th><th>When out</th></tr></thead>
            <tbody>
              {p.variants.map(v => (
                <tr key={v.title}><td>{v.title}</td><td>{v.stock}</td><td>{v.policy === 'CONTINUE' ? 'Pre-order' : 'Sold out'}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Products() {
  const [view, setView] = useState({ name: 'list' });
  if (view.name === 'new') {
    return <NewProduct onCancel={() => setView({ name: 'list' })}
      onCreated={(id, warnings) => setView({ name: 'edit', id, warnings })} />;
  }
  if (view.name === 'edit') {
    return <Editor id={view.id} initialWarnings={view.warnings} onBack={() => setView({ name: 'list' })} />;
  }
  return <ProductList onOpen={(id) => setView({ name: 'edit', id })} onNew={() => setView({ name: 'new' })} />;
}
